// OWNER: Agent C. Searchable, account-grouped holding picker for
// /transactions/new — the screen that matters most, so this has to be fast.
import { Combobox } from "@cloudflare/kumo/components/combobox";
import type { Account, Holding } from "@shared/types";

export interface HoldingOption {
  holding: Holding;
  accountName: string;
  /** Base UI's Combobox auto-detects a `{ value, label }` shape and uses
   * `label` to render the input/list text for object item values — without
   * this the trigger renders the item's JSON instead of its name. */
  label: string;
}

interface Group {
  value: string; // account name, used as the group label
  items: HoldingOption[];
}

export function buildHoldingGroups(accounts: Account[], holdingsByAccount: Map<number, Holding[]>): Group[] {
  return accounts
    .filter((a) => a.isActive)
    .map((a) => ({
      value: a.name,
      items: (holdingsByAccount.get(a.id) ?? [])
        .filter((h) => h.isActive)
        .map((h) => ({ holding: h, accountName: a.name, label: h.name })),
    }))
    .filter((g) => g.items.length > 0);
}

export function HoldingPicker({
  groups,
  value,
  onValueChange,
  error,
}: {
  groups: Group[];
  value: HoldingOption | null;
  onValueChange: (value: HoldingOption | null) => void;
  error?: string;
}) {
  return (
    <Combobox
      items={groups}
      value={value}
      onValueChange={(v) => onValueChange((v as HoldingOption | null) ?? null)}
      isItemEqualToValue={(item: unknown, val: unknown) =>
        (item as HoldingOption).holding?.id === (val as HoldingOption).holding?.id
      }
      label="Holding"
      required
      error={error}
    >
      <Combobox.TriggerInput placeholder="Search holdings…" className="w-full" />
      <Combobox.Content className="max-h-[320px] overflow-y-auto">
        <Combobox.Empty>No holdings match.</Combobox.Empty>
        <Combobox.List>
          {(group: Group) => (
            <Combobox.Group key={group.value} items={group.items}>
              <Combobox.GroupLabel>{group.value}</Combobox.GroupLabel>
              <Combobox.Collection>
                {(item: HoldingOption) => (
                  <Combobox.Item key={item.holding.id} value={item}>
                    {item.label}
                  </Combobox.Item>
                )}
              </Combobox.Collection>
            </Combobox.Group>
          )}
        </Combobox.List>
      </Combobox.Content>
    </Combobox>
  );
}
